import React, { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { motion, LayoutGroup, AnimatePresence } from 'framer-motion';
import confetti from 'canvas-confetti';
import {
    playChime, playTick, playOvertake, playBigOvertake, playStormingUp, playPodium,
    playStreakForLevel, playLevelUp, playAchievement, playMachineTier,
    playMilestone, playBattle,
} from './sounds';
import './leaderboard.css';

const POLL_MS = 5000;
const COLUMNS = [
    { key: 'pickers',  label: 'Pickers' },
    { key: 'pressers', label: 'Pressers' },
    { key: 'packers',  label: 'Packers' },
];

// ─────────────────────────────────────────────────────────────────────
// Clock timezone: display local time for the location, not the browser.
// Substring-matched against the loc name; first hit wins. Fallback ET.
// Order matters when hints could overlap (put the specific ones first).
// ─────────────────────────────────────────────────────────────────────
const TZ_HINTS = [
    // Pacific
    ['Fair Oaks',      'America/Los_Angeles'],
    ['Modesto',        'America/Los_Angeles'],
    ['El Cerrito',     'America/Los_Angeles'],
    ['San Carlos',     'America/Los_Angeles'],
    ['Las Vegas',      'America/Los_Angeles'],
    ['Pleasant Hill',  'America/Los_Angeles'],
    // Central
    ['Brookfield',     'America/Chicago'],       // Brookfield WI
    ['Blaine',         'America/Chicago'],       // Blaine MN
    ['Carrollton',     'America/Chicago'],       // Carrollton TX
    ['Birmingham',     'America/Chicago'],       // Birmingham AL
    ['Austin',         'America/Chicago'],
    ['Arlington TX',   'America/Chicago'],
    ['Minneapolis',    'America/Chicago'],
    // Eastern (default fallback covers everything else)
    // Lancaster, Tinton Falls, Eatontown, Franklin Lakes, Kearny, Cherry Hill,
    // Crofton, Scarsdale, Long Island, Maryland, Raleigh, Cary, Pittsburgh,
    // Mechanicsburg, Huntington Valley, Towson, Dublin (assume CA — see below),
    // Madison NJ, Rochester NY — all ET.
    ['Dublin',         'America/Los_Angeles'],   // Dublin CA
];

function locationTZ(name) {
    if (!name) return 'America/New_York';
    for (const [hint, tz] of TZ_HINTS) {
        if (name.includes(hint)) return tz;
    }
    return 'America/New_York';
}

// Short abbreviation shown next to the clock so operators know which TZ.
function tzAbbrev(tz, now) {
    try {
        const parts = new Intl.DateTimeFormat('en-US', {
            timeZone: tz, timeZoneName: 'short',
        }).formatToParts(now);
        const abbr = parts.find(p => p.type === 'timeZoneName')?.value || '';
        return abbr;
    } catch { return ''; }
}

// ─────────────────────────────────────────────────────────────────────
// ANIMATION KNOBS — tweak these live and reload; nothing else depends
// on their exact values.
// ─────────────────────────────────────────────────────────────────────
const ANIM = {
    ATTENTION_MS: 800,               // shake + scale hold BEFORE the row moves
    ATTENTION_SCALE: 1.15,           // how big a mover-up grows during attention
    ATTENTION_SHAKE_PX: 6,           // amplitude of the pre-move shake
    MOVE_MS: 1200,                   // slide duration — F1 timing tower slow
    MOVE_EASE: [0.16, 1, 0.3, 1],    // expo out — fast start, dramatic slow finish
    LANDING_MS: 350,                 // bounce settle after the slide
    LANDING_OVERSHOOT: 1.06,         // peak of the landing bounce
    GLOW_LINGER_MS: 4000,            // color tint stays after landing
    STAGGER_MS: 200,                 // gap between successive movers-up
    MAX_HIGHLIGHTED: 3,              // cap dramatic sequences per poll cycle
    DOWN_MOVE_MULT: 1.0,             // movers-down use the same duration…
    DOWN_STYLE_INTENSITY: 0.55,      // …but a subdued red tint (60% opacity)
    SEQUENCE_SAFETY_MS: 250,         // tiny buffer after glow linger before next poll can trigger
};
// Derived total sequence length per poll cycle.
const _seqLen = (nHighlighted) => (
    (Math.max(0, nHighlighted - 1) * ANIM.STAGGER_MS) +
    ANIM.ATTENTION_MS +
    ANIM.MOVE_MS +
    ANIM.LANDING_MS +
    ANIM.GLOW_LINGER_MS +
    ANIM.SEQUENCE_SAFETY_MS
);

// ─────────────────────────────────────────────────────────────────────
// BROADCAST CALLOUTS — fight-game / F1-timing overlays.
// Fire on rank changes, escalate with the magnitude of the change.
// ─────────────────────────────────────────────────────────────────────
const CALLOUT = {
    DURATION_MS: 2000,           // how long a callout stays on screen
    STAGGER_MS: 380,             // delay between successive callouts in same column
    MAX_PER_COL: 3,              // hard cap on the queue depth per column
    THRESHOLDS: {
        BIG:    3,               // delta >= 3 → "+N POSITIONS!" banner
        HUGE:   6,               // delta >= 6 → "STORMING UP!"
        PODIUM: 3,               // landing at rank ≤ 3 → "PODIUM!"
    },
};

// Bonus flavor callouts that fire on interesting board states (not on
// rank changes). Each has its own per-column cooldown so a chatty state
// doesn't spam the screen.
const FLAVOR = {
    ENABLED: true,
    COOLDOWN_MS: 3 * 60_000,     // per column, per flavor kind
    CLOSE_RACE_MAX_GAP: 80,      // top-2 within N points → "CLOSE RACE" (scaled 10× 2026-07-13)
    SHUTOUT_MULT:      2.0,      // #1 has 2x #2's score → "SHUTOUT"
    TIED_MIN_RANK:     3,        // only celebrate ties in top-N
    QUIET_MIN_MS: 5 * 60_000,    // no score change for 5+ min → "SECOND WIND" on next bump
};

// ─────────────────────────────────────────────────────────────────────
// STREAK / MOMENTUM — pace-based flame indicators on hot rows.
// Rolling window compared against level thresholds in points-per-minute.
// ─────────────────────────────────────────────────────────────────────
const STREAK = {
    WINDOW_MS: 60_000,                    // rolling 60-second pace window
    STALE_AFTER_MS: 90_000,               // 90s idle → streak drops
    // Thresholds scaled ~8× on 2026-07-13 to track the new scoring model
    // (pickers 10×, pressers 5×, packers 5× — weighted avg ~8×).
    LEVELS: [
        { min: 240,  label: 'HOT',      cls: 'streak--hot',      icon: '🔥' },
        { min: 480,  label: 'ON FIRE',  cls: 'streak--fire',     icon: '🔥🔥' },
        { min: 960,  label: 'BLAZING',  cls: 'streak--blaze',    icon: '🔥🔥🔥' },
    ],
};

const PROGRESS = {
    ANIMATION_MS: 600,
};

const SCORE_POP = {
    DURATION_MS: 500,
    PEAK_SCALE: 1.35,
};

// ─────────────────────────────────────────────────────────────────────
// AMBIENT PARTICLE BACKGROUND — subtle, cheap to run for 10+ hours.
// ─────────────────────────────────────────────────────────────────────
const PARTICLES = {
    COUNT: 80,
    MIN_SIZE: 1.5,
    MAX_SIZE: 4.5,
    MIN_SPEED: 0.10,           // px per frame drift upward
    MAX_SPEED: 0.45,
    MIN_ALPHA: 0.30,
    MAX_ALPHA: 0.85,
    HUE_CYCLE: [190, 200, 210, 45, 45, 145],  // cyan-heavy with gold + emerald
};

// ─────────────────────────────────────────────────────────────────────
// HOURLY SHOWCASE — full-screen "top performers" takeover.
// ─────────────────────────────────────────────────────────────────────
const SHOWCASE = {
    INTERVAL_MIN: 60,          // fire every N minutes
    HOLD_MS: 10_000,           // time on screen
    ENTER_MS: 700,
    EXIT_MS: 500,
    DISABLED: false,           // set true (or add ?noshowcase=1) to skip
};

// ─────────────────────────────────────────────────────────────────────
// COLUMN ICONS — inline SVG so nothing external loads, ever.
// Sized inside the header; styled via currentColor.
// ─────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────
// FEATURE FLAGS — every effect can be toggled via URL param (?noXXX=1)
// or by editing this block. Order matches the build-order priority.
// ─────────────────────────────────────────────────────────────────────
const FEATURES = {
    SOUND:          true,   // ?nosound=1
    SOUND_AMBIENT:  false,  // ?ambient=1 to opt back in — was too droney
    SOUND_TICK:     true,   // ?notick=1     score-up ticks
    SOUND_WHOOSH:   true,   // ?nowhoosh=1   overtake whoosh
    SOUND_ROAR:     true,   // ?noroar=1     new-leader roar
    SOUND_SIZZLE:   true,   // ?nosizzle=1   ON FIRE sizzle
    KILL_FEED:      false,  // ?nofeed=1 (default OFF; enable with ?feed=1)
    ACHIEVEMENTS:   true,   // ?noachievements=1  trophy popups
    AVATARS:        true,   // ?noavatars=1  initials chip on rows
    LEVELS:         true,   // ?nolevels=1   level badge per row + level-up flash
    TITLES:         true,   // ?notitles=1   earned titles under names
    NARRATIVE:      true,   // ?nonarrative=1 rotating context line in header
    MILESTONE:      true,   // ?nomilestone=1 board-wide milestone takeovers
    SPOTLIGHT:      true,   // ?nospotlight=1 periodic camera-zoom moments
    RIVALRIES:      true,   // ?norivalries=1 auto rivalries + BATTLE cut-in
};

const KILL_FEED = {
    MAX_ENTRIES: 40,
    SCROLL_DURATION_MS: 32_000,   // slower = readable from across the room
};

const ACHIEVEMENT = {
    POPUP_MS: 3800,
    STAGGER_MS: 400,
    MAX_QUEUE: 5,
    // Each achievement is defined below inline; thresholds here for tweak:
    // Scaled 10× on 2026-07-13 with the new scoring model — 100 pts is
    // now a few picks and would fire in the first 60 seconds of the day.
    CENTURY_MIN: 1000,
    HAT_TRICK_WINDOW_MS: 60 * 60_000,
    HAT_TRICK_COUNT: 3,
    COMEBACK_FROM: 11,
    COMEBACK_TO: 5,
};

// Per-role level scales — rescaled 2026-07-13 to match the new scoring
// (pickers 5/emb + 10/garment, pressers 10/emb, packers 5/emb + 5/garment,
// 10 bonus per completed order-loc).
// Multipliers vs. the 2026-07-08 calibration (which used 0.5/1/2 weights):
//   Pickers  ×10 (0.5→5 emb, 1→10 garment)
//   Pressers ×5  (2→10 emb; garments dropped from scoring)
//   Packers  ×5  (1→5 emb, 1→5 garment)
// The "MACHINE / MAX MACHINE / GOD MODE" meaning stays identical — the
// same amount of work still lands at the same tier.
const LEVEL = {
    POINTS_PER_LEVEL: {
        pickers:  1500,  // was 150 × 10
        pressers:  165,  // was 33  × 5
        packers:   325,  // was 65  × 5
    },
    FLASH_MS: 900,
};

// Machine progression — six tiers, all pegged to LEVEL count in the role.
//   HALF MACHINE        = 4 levels  ("warming up")
//   THE MACHINE         = 8 levels  ("crushing it")
//   MAX MACHINE         = 12 levels ("record-day pace")
//   MEGA MACHINE        = 16 levels ("all-time")
//   ULTRA SUPER MACHINE = 20 levels ("myth")
//   GOD MODE            = 28 levels ("ultimate — confetti + roar + halo")
// Values fall out of the per-role points-per-level table, so the effort
// meaning of each tier is identical across pickers/pressers/packers.
const TITLES = {
    HALF_MACHINE_MIN_SCORE: {
        pickers:  6000,   // 4 × 1500
        pressers:  660,   // 4 × 165
        packers:  1700,   // 4 × 325 × 1.3 (packer thresholds +30%)
    },
    MACHINE_MIN_SCORE: {
        pickers:  12000,  // 8 × 1500
        pressers:  1320,  // 8 × 165
        packers:   3400,  // 8 × 325 × 1.3
    },
    MAX_MACHINE_MIN_SCORE: {
        pickers:  18000,  // 12 × 1500
        pressers:  1980,  // 12 × 165
        packers:   5075,  // 12 × 325 × 1.3
    },
    MEGA_MACHINE_MIN_SCORE: {
        pickers:  24000,  // 16 × 1500
        pressers:  2640,  // 16 × 165
        packers:   6750,  // 16 × 325 × 1.3
    },
    ULTRA_SUPER_MACHINE_MIN_SCORE: {
        pickers:  36000,  // 20 × 1500 × 1.2 (+20%)
        pressers:  3960,  // 20 × 165  × 1.2
        packers:  10150,  // 20 × 325  × 1.3 × 1.2
    },
    GOD_MODE_MIN_SCORE: {
        pickers:  54600,  // 28 × 1500 × 1.3 (+30%)
        pressers:  6000,  // 28 × 165  × 1.3
        packers:  15375,  // 28 × 325  × 1.3 × 1.3
    },
    BLAZING_LEVEL_IDX: 2,           // BLAZING = "SPEED DEMON"
};

const NARRATIVE = {
    ROTATE_MS: 12_000,
};

const MILESTONE = {
    // Combined-score thresholds. Sum across all 3 columns of every row's score.
    // Two tiers:
    //   REGULAR — quick "nice work" takeover, 4.5s hold, roar sound.
    //   GRAND   — every 25k, 7s hold, confetti + trophy chime + roar layered.
    // First 3 special thresholds (500 / 1k / 2.5k) trigger the very first
    // regular milestones of the day.
    REGULAR_HOLD_MS: 4500,
    GRAND_HOLD_MS:   7000,
    // First-mount guard: any threshold already ≤ initial total is marked
    // "already crossed" silently so a mid-day page load doesn't fire five
    // takeovers back-to-back on first poll.
    SUPPRESS_ON_MOUNT: true,
    ENABLED: true,
};
// Build the threshold lists once at module load.
(function buildThresholds() {
    const grand = [];
    // 2026-07-13 rescale: combined-board scores now ~8× the old model
    // (pickers 10×, pressers 5×, packers 5× — weighted by their share of a
    // typical day). Scale MILESTONE the same 8× so celebrations keep their
    // rhythm instead of firing every minute.
    const SCALE = 8;
    for (let i = 1; i <= 40; i++) grand.push(i * 25_000 * SCALE);   // 200k, 400k, ..., 8M
    const regular = [500 * SCALE, 1000 * SCALE, 2500 * SCALE];
    for (let v = 5_000 * SCALE; v <= 1_000_000 * SCALE; v += 5_000 * SCALE) {
        if (v % (25_000 * SCALE) !== 0) regular.push(v);           // every 40k, skipping grand ones
    }
    MILESTONE.REGULAR_THRESHOLDS = regular;
    MILESTONE.GRAND_THRESHOLDS = grand;
    MILESTONE.ALL_SORTED = regular.concat(grand).sort((a, b) => a - b);
    MILESTONE.IS_GRAND = new Set(grand);
})();

const RIVALRY = {
    // Number of head-to-head rank swaps in a day required to flag a pair as rivals.
    SWAP_THRESHOLD: 3,
    // Rivals within this many points → eligible for the BATTLE cut-in.
    BATTLE_DIFF: 250,   // Scaled 10× 2026-07-13 with the new scoring model.
    // Full-screen BATTLE hold duration. Matches the length of the epic
    // battle-horn SFX (13.0s) so the overlay stays on until the sound ends.
    BATTLE_HOLD_MS: 13000,
    // Minimum time between BATTLE cut-ins for the SAME pair (spam guard).
    BATTLE_COOLDOWN_MS: 10 * 60_000,
    ENABLED: true,
};

const SPOTLIGHT = {
    MIN_INTERVAL_MS: 20 * 60_000,
    MAX_INTERVAL_MS: 30 * 60_000,
    HOLD_MS: 6000,
    ENABLED: true,
};

// ─────────────────────────────────────────────────────────────────────
// AUTO SCROLL — each column ticks up and down on a rAF loop so all 15
// rows get on-screen time. Jumps to a specific user when something
// happens to them (rank change, level up, streak, achievement, machine
// tier crossed), then holds them centered for HOLD_MS before resuming.
// ─────────────────────────────────────────────────────────────────────
const AUTO_SCROLL = {
    ENABLED: true,
    PX_PER_FRAME: 2.016,            // 1.5x from 1.344 (~120 px/sec at 60fps)
    JUMP_HOLD_MS: 5000,             // pause after jump-to-user
    JUMP_ON_EVENTS: true,           // set false to disable jumps entirely
    TOP_PAUSE_MS: 3000,             // linger 3s at top before starting down
    BOTTOM_PAUSE_MS: 3000,          // linger 3s at bottom before scrolling up
};

const COLUMN_ICONS = {
    pickers: (
        <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M3 4h2l2 12h11l2-8H6" />
            <circle cx="9" cy="20" r="1.6" />
            <circle cx="17" cy="20" r="1.6" />
        </svg>
    ),
    pressers: (
        <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            {/* Shirt silhouette with a heat swoosh */}
            <path d="M4 7l4-3 4 2 4-2 4 3-2 4-2-1v10H6V10L4 11z" />
            <path d="M10 13c1 1 3 1 4 0" opacity="0.7" />
        </svg>
    ),
    packers: (
        <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M3 7l9-4 9 4v10l-9 4-9-4z" />
            <path d="M3 7l9 4 9-4" />
            <path d="M12 11v10" />
            <path d="M7.5 5.2v4l3-1.5" opacity="0.7" />
        </svg>
    ),
};

// Row phases (per row name, per column):
//   idle       — no animation active
//   attention  — shake + scale in place, PRE-move
//   moving     — Framer layout animation actively sliding
//   landing    — bounce settle
//   glow       — glow linger after landing
//   drop       — mover-down subdued red glow
function RankRow({ row, prevRank, phase, delta, progressPct, streak, showAvatar, level, levelFlash, title, inBattle }) {
    const isTop3 = row.rank <= 3;
    const rankClass = isTop3 ? `rank-chip rank-chip--gold-${row.rank}` : 'rank-chip';

    let animate = {};
    let transition = { layout: { duration: ANIM.MOVE_MS / 1000, ease: ANIM.MOVE_EASE } };
    if (phase === 'attention') {
        animate = {
            scale: ANIM.ATTENTION_SCALE,
            x: [0, -ANIM.ATTENTION_SHAKE_PX, ANIM.ATTENTION_SHAKE_PX,
                -ANIM.ATTENTION_SHAKE_PX + 2, ANIM.ATTENTION_SHAKE_PX - 2,
                -3, 3, 0],
        };
        transition = { duration: ANIM.ATTENTION_MS / 1000, ease: 'easeInOut' };
    } else if (phase === 'moving') {
        animate = { scale: ANIM.ATTENTION_SCALE, x: 0 };
        transition = { layout: { duration: ANIM.MOVE_MS / 1000, ease: ANIM.MOVE_EASE } };
    } else if (phase === 'landing') {
        animate = { scale: [ANIM.ATTENTION_SCALE, 0.96, ANIM.LANDING_OVERSHOOT, 1.0], x: 0 };
        transition = { duration: ANIM.LANDING_MS / 1000, ease: 'easeOut', times: [0, 0.4, 0.75, 1] };
    } else {
        animate = { scale: 1, x: 0 };
    }

    const classNames = [
        'board-row',
        row.rank === 1 && 'board-row--leader',
        row.rank === 1 && 'board-row--rank1',
        row.rank === 2 && 'board-row--rank2',
        row.rank === 3 && 'board-row--rank3',
        streak && `board-row--streak ${streak.cls}`,
        phase === 'attention' && 'board-row--attention',
        phase === 'moving' && 'board-row--charging',
        phase === 'landing' && 'board-row--landing',
        phase === 'glow' && 'board-row--glow',
        phase === 'drop' && 'board-row--drop',
    ].filter(Boolean).join(' ');

    return (
        <motion.div
            layout
            layoutId={row.name}
            animate={animate}
            transition={transition}
            className={classNames}
            style={{ zIndex: (phase !== 'idle' && phase != null) ? 5 : 1 }}
        >
            <div className={rankClass}>
                {row.rank === 1 ? <span className="rank-chip__crown">♛</span> : row.rank}
            </div>
            <div className="board-name">
                {showAvatar && <Avatar name={row.name} />}
                <div className="board-name__stack">
                    <div className="board-name__row">
                        <span className="board-name__label">{row.name}</span>
                        {streak && (
                            <span className={`streak-badge ${streak.cls}`}>
                                <span className="streak-badge__icon">{streak.icon}</span>
                                <span className="streak-badge__label">{streak.label}</span>
                            </span>
                        )}
                        {level != null && (
                            <span className={`level-badge${levelFlash ? ' level-badge--flash' : ''}`}>LV {level}</span>
                        )}
                        {inBattle && (
                            <span className="battle-badge">
                                <span className="battle-badge__icon">⚔</span>
                                <span className="battle-badge__label">BATTLE</span>
                            </span>
                        )}
                    </div>
                    {/* Always render the title slot so every row has the same height.
                        When no title is earned, use a non-breaking space so the
                        line has intrinsic height but nothing visible. */}
                    <div
                        className={`board-name__title${title ? '' : ' board-name__title--empty'}`}
                        data-title={title || ''}
                    >
                        {title || ' '}
                    </div>
                </div>
            </div>
            <div className="board-score">
                <motion.span
                    key={row.score}
                    initial={{ scale: SCORE_POP.PEAK_SCALE, color: '#10b981' }}
                    animate={{ scale: 1, color: 'inherit' }}
                    transition={{ duration: SCORE_POP.DURATION_MS / 1000, ease: 'easeOut' }}
                >
                    {Math.round(row.score)}
                </motion.span>
            </div>
            <div className={`board-delta ${delta > 0 ? 'up' : delta < 0 ? 'down' : ''}`}>
                {delta > 0 ? `▲ ${delta}` : delta < 0 ? `▼ ${-delta}` : ''}
            </div>
            {/* Anticipation bar — how close this row is to overtaking the row above. */}
            <div className="board-row__progress" aria-hidden="true">
                <motion.div
                    className="board-row__progress-fill"
                    animate={{ width: `${Math.round((progressPct || 0) * 100)}%` }}
                    transition={{ duration: PROGRESS.ANIMATION_MS / 1000, ease: 'easeOut' }}
                />
            </div>
        </motion.div>
    );
}

// ─────────────────────────────────────────────────────────────────────
// ARCADE SYNTH — Web Audio procedural sounds for effects we don't have
// MP3s for. One AudioContext for the whole page; nodes are torn down
// after each one-shot so nothing leaks over a 10-hour session.
// ─────────────────────────────────────────────────────────────────────
class ArcadeSynth {
    constructor() {
        this.ctx = null;
        this.ambient = null;
    }
    _ctx() {
        if (!this.ctx) {
            const AC = window.AudioContext || window.webkitAudioContext;
            if (!AC) return null;
            this.ctx = new AC();
        }
        if (this.ctx.state === 'suspended') this.ctx.resume().catch(() => {});
        return this.ctx;
    }
    _envelope(dur, peak = 0.35) {
        const c = this._ctx(); if (!c) return null;
        const g = c.createGain();
        const now = c.currentTime;
        g.gain.setValueAtTime(0.0001, now);
        g.gain.exponentialRampToValueAtTime(peak, now + 0.02);
        g.gain.exponentialRampToValueAtTime(0.0001, now + dur);
        g.connect(c.destination);
        return { g, now, dur };
    }
    _noiseBuf(dur) {
        const c = this._ctx(); if (!c) return null;
        const buf = c.createBuffer(1, Math.floor(c.sampleRate * dur), c.sampleRate);
        const d = buf.getChannelData(0);
        for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
        return buf;
    }
    whoosh() {
        const c = this._ctx(); if (!c) return;
        const dur = 0.45;
        const env = this._envelope(dur, 0.30);
        const buf = this._noiseBuf(dur);
        const src = c.createBufferSource(); src.buffer = buf;
        const bp = c.createBiquadFilter(); bp.type = 'bandpass'; bp.Q.value = 8;
        bp.frequency.setValueAtTime(300, env.now);
        bp.frequency.exponentialRampToValueAtTime(3200, env.now + dur - 0.05);
        src.connect(bp); bp.connect(env.g);
        src.start(env.now); src.stop(env.now + dur);
    }
    sizzle() {
        const c = this._ctx(); if (!c) return;
        const dur = 0.65;
        const env = this._envelope(dur, 0.20);
        const buf = this._noiseBuf(dur);
        const src = c.createBufferSource(); src.buffer = buf;
        const hp = c.createBiquadFilter(); hp.type = 'highpass'; hp.frequency.value = 2400;
        // Amplitude modulation for that fire crackle
        const am = c.createGain(); am.gain.value = 0.7;
        const lfo = c.createOscillator(); lfo.frequency.value = 22;
        const lfoAmt = c.createGain(); lfoAmt.gain.value = 0.5;
        lfo.connect(lfoAmt); lfoAmt.connect(am.gain);
        src.connect(hp); hp.connect(am); am.connect(env.g);
        lfo.start(env.now); lfo.stop(env.now + dur);
        src.start(env.now); src.stop(env.now + dur);
    }
    roar() {
        const c = this._ctx(); if (!c) return;
        const dur = 1.4;
        const env = this._envelope(dur, 0.45);
        const buf = this._noiseBuf(dur);
        const src = c.createBufferSource(); src.buffer = buf;
        const lp = c.createBiquadFilter(); lp.type = 'lowpass';
        lp.frequency.setValueAtTime(400, env.now);
        lp.frequency.linearRampToValueAtTime(1200, env.now + 0.3);
        lp.frequency.linearRampToValueAtTime(800, env.now + dur);
        // A low tone underneath for the "hit"
        const hit = c.createOscillator(); hit.type = 'sawtooth'; hit.frequency.value = 55;
        const hitG = c.createGain();
        hitG.gain.setValueAtTime(0.001, env.now);
        hitG.gain.exponentialRampToValueAtTime(0.6, env.now + 0.03);
        hitG.gain.exponentialRampToValueAtTime(0.001, env.now + 0.6);
        hit.connect(hitG); hitG.connect(c.destination);
        src.connect(lp); lp.connect(env.g);
        src.start(env.now); src.stop(env.now + dur);
        hit.start(env.now); hit.stop(env.now + 0.6);
    }
    trophy() {
        // Rising arpeggio for achievements
        const c = this._ctx(); if (!c) return;
        const notes = [523.25, 659.25, 783.99, 1046.5]; // C E G C
        notes.forEach((f, i) => {
            const t = c.currentTime + i * 0.09;
            const osc = c.createOscillator(); osc.type = 'triangle'; osc.frequency.value = f;
            const g = c.createGain();
            g.gain.setValueAtTime(0.0001, t);
            g.gain.exponentialRampToValueAtTime(0.28, t + 0.015);
            g.gain.exponentialRampToValueAtTime(0.0001, t + 0.35);
            osc.connect(g); g.connect(c.destination);
            osc.start(t); osc.stop(t + 0.4);
        });
    }
    startAmbient() {
        const c = this._ctx(); if (!c || this.ambient) return;
        const nodes = [];
        // Two detuned low sines + subtle noise wash for arena bed
        [{ f: 55, g: 0.020 }, { f: 82.4, g: 0.014 }, { f: 138.6, g: 0.008 }].forEach(({ f, g }) => {
            const osc = c.createOscillator(); osc.type = 'sine'; osc.frequency.value = f;
            const gain = c.createGain(); gain.gain.value = g;
            osc.connect(gain); gain.connect(c.destination);
            osc.start();
            nodes.push({ osc, gain });
        });
        // Faint pink noise bed
        const buf = this._noiseBuf(4);
        const src = c.createBufferSource(); src.buffer = buf; src.loop = true;
        const lp = c.createBiquadFilter(); lp.type = 'lowpass'; lp.frequency.value = 220;
        const nG = c.createGain(); nG.gain.value = 0.010;
        src.connect(lp); lp.connect(nG); nG.connect(c.destination);
        src.start();
        this.ambient = { nodes, src, nG };
    }
    stopAmbient() {
        if (!this.ambient) return;
        try {
            this.ambient.nodes.forEach(n => n.osc.stop());
            this.ambient.src.stop();
        } catch (_) {}
        this.ambient = null;
    }
}
const synth = new ArcadeSynth();

// Ambient particle background — one canvas, one rAF loop, ~40 particles.
// Never allocates during animation (fixed-size buffer, particles recycled).
function ParticleBackground() {
    const canvasRef = useRef(null);
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        let raf = 0;
        let running = true;

        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        function resize() {
            canvas.width = window.innerWidth * dpr;
            canvas.height = window.innerHeight * dpr;
            canvas.style.width = window.innerWidth + 'px';
            canvas.style.height = window.innerHeight + 'px';
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        }
        resize();
        window.addEventListener('resize', resize);

        // Fixed-size particle pool
        const N = PARTICLES.COUNT;
        const parts = new Array(N);
        function seed(p, spawnBottom) {
            p.x = Math.random() * window.innerWidth;
            p.y = spawnBottom ? window.innerHeight + Math.random() * 40
                             : Math.random() * window.innerHeight;
            p.size = PARTICLES.MIN_SIZE + Math.random() * (PARTICLES.MAX_SIZE - PARTICLES.MIN_SIZE);
            p.speed = PARTICLES.MIN_SPEED + Math.random() * (PARTICLES.MAX_SPEED - PARTICLES.MIN_SPEED);
            p.alpha = PARTICLES.MIN_ALPHA + Math.random() * (PARTICLES.MAX_ALPHA - PARTICLES.MIN_ALPHA);
            p.hue = PARTICLES.HUE_CYCLE[Math.floor(Math.random() * PARTICLES.HUE_CYCLE.length)];
            p.twinklePhase = Math.random() * Math.PI * 2;
        }
        for (let i = 0; i < N; i++) { parts[i] = {}; seed(parts[i], false); }

        function frame() {
            if (!running) return;
            const w = window.innerWidth, h = window.innerHeight;
            ctx.clearRect(0, 0, w, h);
            const t = performance.now() * 0.001;
            for (let i = 0; i < N; i++) {
                const p = parts[i];
                p.y -= p.speed;
                p.twinklePhase += 0.02;
                if (p.y < -10) { seed(p, true); continue; }
                const flick = 0.75 + 0.25 * Math.sin(t * 2 + p.twinklePhase);
                ctx.beginPath();
                ctx.fillStyle = `hsla(${p.hue}, 90%, 70%, ${p.alpha * flick})`;
                ctx.shadowColor = `hsla(${p.hue}, 100%, 60%, ${0.5 * p.alpha})`;
                ctx.shadowBlur = 8;
                ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
                ctx.fill();
            }
            ctx.shadowBlur = 0;
            raf = requestAnimationFrame(frame);
        }
        raf = requestAnimationFrame(frame);
        // Pause when the tab is hidden (browsers throttle anyway but skip work).
        function vis() { running = document.visibilityState === 'visible'; if (running) raf = requestAnimationFrame(frame); }
        document.addEventListener('visibilitychange', vis);
        return () => {
            running = false;
            cancelAnimationFrame(raf);
            window.removeEventListener('resize', resize);
            document.removeEventListener('visibilitychange', vis);
        };
    }, []);
    return <canvas ref={canvasRef} className="board-particles" aria-hidden="true" />;
}

// Full-screen "Top Performers" takeover — fires periodically, 10s hold.
function Showcase({ data, onDismiss }) {
    // 2026-08-19: the auto-dismiss timer used to depend on `onDismiss`, but
    // the parent passes an inline arrow (`() => setShowcaseVisible(false)`)
    // which is a new function reference on every parent render. Rankings
    // polls constantly, so the effect kept resetting itself and the timer
    // never fired — showcase stayed on screen until manual refresh.
    // Route onDismiss through a ref and run the timer once on mount so a
    // parent re-render can't cancel it.
    const dismissRef = useRef(onDismiss);
    dismissRef.current = onDismiss;
    useEffect(() => {
        const t = setTimeout(() => dismissRef.current?.(), SHOWCASE.HOLD_MS);
        return () => clearTimeout(t);
    }, []);
    const top3 = COLUMNS.map(col => ({
        col,
        rows: (data?.columns?.[col.key] || []).slice(0, 3),
    }));
    return (
        <motion.div
            className="showcase"
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 1.04 }}
            transition={{ duration: SHOWCASE.ENTER_MS / 1000, ease: [0.16, 1, 0.3, 1] }}
        >
            <div className="showcase__eyebrow">Top Performers</div>
            <div className="showcase__title">{data?.location_name || 'Rankings'}</div>
            <div className="showcase__grid">
                {top3.map(({ col, rows }) => (
                    <div key={col.key} className="showcase__col">
                        <div className="showcase__col-title">
                            <span className="showcase__col-icon">{COLUMN_ICONS[col.key]}</span>
                            {col.label}
                        </div>
                        {rows.map((r, i) => (
                            <motion.div
                                key={r.name}
                                className={`showcase__row showcase__row--rank${i + 1}`}
                                initial={{ y: 40, opacity: 0 }}
                                animate={{ y: 0, opacity: 1 }}
                                transition={{ delay: 0.2 + i * 0.15, duration: 0.5, ease: 'easeOut' }}
                            >
                                <div className={`showcase__medal showcase__medal--r${i + 1}`}>{i + 1}</div>
                                <div className="showcase__row-body">
                                    <div className="showcase__name">{r.name}</div>
                                    <div className="showcase__score">{Math.round(r.score)}</div>
                                </div>
                            </motion.div>
                        ))}
                        {rows.length === 0 && <div className="showcase__empty">—</div>}
                    </div>
                ))}
            </div>
        </motion.div>
    );
}

// ─────────────────────────────────────────────────────────────────────
// PLAYER IDENTITY — initials chip with a color derived from the name so
// the same person always gets the same badge across polls and columns.
// ─────────────────────────────────────────────────────────────────────
function nameHash(name) {
    let h = 0;
    for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) | 0;
    return Math.abs(h);
}
function initialsFor(name) {
    if (!name) return '?';
    const parts = name.replace(/[^\w\s]/g, '').split(/\s+/).filter(Boolean);
    return ((parts[0]?.[0] || '') + (parts[1]?.[0] || parts[0]?.[1] || '')).toUpperCase();
}
function Avatar({ name }) {
    const h = nameHash(name);
    const hue = h % 360;
    const style = {
        background: `linear-gradient(135deg, hsl(${hue}, 65%, 45%), hsl(${(hue + 40) % 360}, 65%, 30%))`,
        boxShadow: `0 0 12px hsla(${hue}, 80%, 55%, 0.35), inset 0 0 6px rgba(0,0,0,0.4)`,
    };
    return (
        <span className="board-avatar" style={style} aria-hidden="true">
            {initialsFor(name)}
        </span>
    );
}

// ─────────────────────────────────────────────────────────────────────
// KILL FEED — bottom-edge scrolling ticker of the day's events.
// Ring buffer capped at KILL_FEED.MAX_ENTRIES so RAM stays bounded.
// ─────────────────────────────────────────────────────────────────────
function KillFeed({ items, tzForClock }) {
    if (!items || items.length === 0) return (
        <div className="killfeed">
            <div className="killfeed__label">Feed</div>
            <div className="killfeed__scroll"><span className="killfeed__quiet">Board is warming up…</span></div>
        </div>
    );
    // Duplicate items once so the marquee wraps seamlessly.
    return (
        <div className="killfeed">
            <div className="killfeed__label">Feed</div>
            <div className="killfeed__scroll">
                <div className="killfeed__track">
                    {[...items, ...items].map((e, i) => {
                        const timeStr = new Date(e.ts).toLocaleTimeString('en-US', {
                            timeZone: tzForClock, hour: '2-digit', minute: '2-digit',
                        });
                        return (
                            <span key={`${e.id}-${i}`} className={`killfeed__ev killfeed__ev--${e.kind}`}>
                                <span className="killfeed__ts">{timeStr}</span>
                                <span className="killfeed__body">{e.text}</span>
                            </span>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────
// ACHIEVEMENT POPUP — Xbox-style trophy card sliding in from bottom-left.
// One card visible at a time; queue is FIFO with STAGGER_MS gap.
// ─────────────────────────────────────────────────────────────────────
function AchievementPopup({ payload }) {
    if (!payload) return null;
    return (
        <motion.div
            key={payload.id}
            className={`trophy trophy--${payload.tone || 'gold'}`}
            initial={{ x: -420, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -420, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 260, damping: 22 }}
        >
            <div className="trophy__icon" aria-hidden="true">🏆</div>
            <div className="trophy__body">
                <div className="trophy__label">{payload.label}</div>
                <div className="trophy__name">{payload.name}</div>
                {payload.sub && <div className="trophy__sub">{payload.sub}</div>}
            </div>
        </motion.div>
    );
}

// ─────────────────────────────────────────────────────────────────────
// MILESTONE TAKEOVER — brief full-screen team-win moment.
// ─────────────────────────────────────────────────────────────────────
function MilestoneTakeover({ payload, locationName }) {
    if (!payload) return null;
    const grand = !!payload.grand;
    return (
        <motion.div
            key={payload.id}
            className={`milestone${grand ? ' milestone--grand' : ''}`}
            initial={{ opacity: 0, scale: grand ? 0.75 : 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 1.08 }}
            transition={{ duration: grand ? 0.6 : 0.45, ease: [0.16, 1, 0.3, 1] }}
        >
            <div className="milestone__eyebrow">
                {grand ? 'Grand Slam · Team Milestone' : 'Team Milestone'}
            </div>
            <div className="milestone__value">{payload.threshold.toLocaleString()}</div>
            <div className="milestone__label">Combined Points</div>
            <div className="milestone__loc">{locationName}</div>
        </motion.div>
    );
}

// ─────────────────────────────────────────────────────────────────────
// SPOTLIGHT — camera-zoom moment on a random top performer.
// ─────────────────────────────────────────────────────────────────────
function Spotlight({ payload }) {
    if (!payload) return null;
    return (
        <motion.div
            className="spotlight"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
        >
            <motion.div
                className="spotlight__card"
                initial={{ scale: 0.5, y: 60 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.6, y: 40 }}
                transition={{ type: 'spring', stiffness: 240, damping: 22 }}
            >
                <div className="spotlight__eyebrow">Spotlight · {payload.colLabel}</div>
                <div className="spotlight__avatar">
                    <Avatar name={payload.name} />
                </div>
                <div className="spotlight__name">{payload.name}</div>
                <div className="spotlight__stat">
                    <div className="spotlight__stat-value">{Math.round(payload.score)}</div>
                    <div className="spotlight__stat-label">Points · Rank #{payload.rank}</div>
                </div>
            </motion.div>
        </motion.div>
    );
}

// ─────────────────────────────────────────────────────────────────────
// BATTLE — boxing "tale-of-the-tape" full-screen cut-in on rivals.
// ─────────────────────────────────────────────────────────────────────
function Battle({ payload }) {
    if (!payload) return null;
    const { leader, chaser, colLabel, swaps } = payload;
    const gap = Math.round(leader.score - chaser.score);
    return (
        <motion.div
            className="battle"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
        >
            <div className="battle__eyebrow">Head to Head · {colLabel}</div>
            <div className="battle__title">BATTLE</div>
            <div className="battle__grid">
                <motion.div
                    className="battle__fighter battle__fighter--leader"
                    initial={{ x: -200, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ type: 'spring', stiffness: 220, damping: 22 }}
                >
                    <div className="battle__medal">1</div>
                    <div className="battle__avatar"><Avatar name={leader.name} /></div>
                    <div className="battle__name">{leader.name}</div>
                    <div className="battle__score">{Math.round(leader.score)}</div>
                </motion.div>
                <motion.div
                    className="battle__vs"
                    initial={{ scale: 0.4, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ delay: 0.15, type: 'spring', stiffness: 260, damping: 18 }}
                >
                    VS
                </motion.div>
                <motion.div
                    className="battle__fighter battle__fighter--chaser"
                    initial={{ x: 200, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ type: 'spring', stiffness: 220, damping: 22 }}
                >
                    <div className="battle__medal">2</div>
                    <div className="battle__avatar"><Avatar name={chaser.name} /></div>
                    <div className="battle__name">{chaser.name}</div>
                    <div className="battle__score">{Math.round(chaser.score)}</div>
                </motion.div>
            </div>
            <div className="battle__stats">
                <span>Gap <strong>{gap}</strong></span>
                <span className="battle__stats-sep">·</span>
                <span>{swaps} swaps today</span>
            </div>
        </motion.div>
    );
}

// Broadcast-style callout overlay pinned to a column.
function Callout({ payload }) {
    if (!payload) return null;
    return (
        <motion.div
            key={payload.id}
            className={`callout callout--${payload.kind}`}
            initial={{ x: '-140%', opacity: 0, skewX: '-12deg' }}
            animate={{ x: '0%', opacity: 1, skewX: '-12deg' }}
            exit={{ x: '120%', opacity: 0, skewX: '-12deg' }}
            transition={{ type: 'spring', stiffness: 380, damping: 26 }}
        >
            <div className="callout__label">{payload.label}</div>
            <div className="callout__name">{payload.name}</div>
            {payload.sub && <div className="callout__sub">{payload.sub}</div>}
        </motion.div>
    );
}

function EmptyRow() {
    return <div className="board-row board-row--ghost"><div /><div /><div /><div /></div>;
}

export default function RankingsDisplay() {
    const { token } = useParams();
    const [searchParams] = useSearchParams();
    // `data` is what's DISPLAYED. The next-poll response is held in
    // `pendingDataRef` until the current animation sequence releases it.
    const [data, setData] = useState(null);
    const [error, setError] = useState('');
    const [pollAlive, setPollAlive] = useState(true);
    // Phases per (colKey, rowName) — drives per-row visuals.
    const [phases, setPhases] = useState({});
    const prevSnapshotRef = useRef({ pickers: {}, pressers: {}, packers: {} });
    const [clockNow, setClockNow] = useState(new Date());
    const muted = searchParams.get('mute') === '1';

    // Anim scheduler state
    const animBusyUntilRef = useRef(0);
    const pendingDataRef = useRef(null);
    const scheduledTimeoutsRef = useRef([]);

    // Per-column scroll state (position, direction, pause window).
    // Shape: { pickers: { pos, dir, pauseUntil, listEl }, ... }
    const scrollStateRef = useRef({
        pickers:  { pos: 0, dir: 1, pauseUntil: 0, listEl: null },
        pressers: { pos: 0, dir: 1, pauseUntil: 0, listEl: null },
        packers:  { pos: 0, dir: 1, pauseUntil: 0, listEl: null },
    });
    const listRefs = {
        pickers:  useRef(null),
        pressers: useRef(null),
        packers:  useRef(null),
    };
    // Inner track that gets translateY'd. scrollTop was refusing to move so
    // we translate the inner div instead — always works.
    const trackRefs = {
        pickers:  useRef(null),
        pressers: useRef(null),
        packers:  useRef(null),
    };

    // Score history per (col::name) → [{score, ts}, ...] for streak/pace math.
    const scoreHistoryRef = useRef({});
    // Broadcast callouts: per-column queue. Only the head element is displayed.
    const [calloutsByCol, setCalloutsByCol] = useState({ pickers: [], pressers: [], packers: [] });
    // Showcase overlay
    const [showcaseVisible, setShowcaseVisible] = useState(false);
    const noshowcase = searchParams.get('noshowcase') === '1';

    // ─── Feature flag resolution (URL overrides in-code defaults) ──
    const flags = {
        SOUND:         FEATURES.SOUND && searchParams.get('nosound') !== '1' && !muted,
        AMBIENT:       (FEATURES.SOUND_AMBIENT || searchParams.get('ambient') === '1') && searchParams.get('noambient') !== '1' && !muted,
        TICK:          FEATURES.SOUND_TICK && searchParams.get('notick') !== '1' && !muted,
        WHOOSH:        FEATURES.SOUND_WHOOSH && searchParams.get('nowhoosh') !== '1' && !muted,
        ROAR:          FEATURES.SOUND_ROAR && searchParams.get('noroar') !== '1' && !muted,
        SIZZLE:        FEATURES.SOUND_SIZZLE && searchParams.get('nosizzle') !== '1' && !muted,
        KILL_FEED:     (FEATURES.KILL_FEED || searchParams.get('feed') === '1') && searchParams.get('nofeed') !== '1',
        ACHIEVEMENTS:  FEATURES.ACHIEVEMENTS && searchParams.get('noachievements') !== '1',
        AVATARS:       FEATURES.AVATARS && searchParams.get('noavatars') !== '1',
        LEVELS:        FEATURES.LEVELS && searchParams.get('nolevels') !== '1',
        TITLES:        FEATURES.TITLES && searchParams.get('notitles') !== '1',
        NARRATIVE:     FEATURES.NARRATIVE && searchParams.get('nonarrative') !== '1',
        MILESTONE:     FEATURES.MILESTONE && searchParams.get('nomilestone') !== '1',
        SPOTLIGHT:     FEATURES.SPOTLIGHT && searchParams.get('nospotlight') !== '1',
        RIVALRIES:     FEATURES.RIVALRIES && searchParams.get('norivalries') !== '1',
    };

    // Kill feed
    const [killFeed, setKillFeed] = useState([]);
    const pushFeed = (kind, text) => {
        setKillFeed(prev => {
            const next = [{ id: `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, kind, text, ts: Date.now() }, ...prev];
            return next.slice(0, KILL_FEED.MAX_ENTRIES);
        });
    };

    // Achievement popup queue (FIFO). Timers self-clean via scheduledTimeoutsRef.
    const [trophy, setTrophy] = useState(null);
    const trophyQueueRef = useRef([]);
    const trophyBusyRef = useRef(false);
    const showNextTrophy = () => {
        if (trophyBusyRef.current) return;
        const next = trophyQueueRef.current.shift();
        if (!next) return;
        trophyBusyRef.current = true;
        setTrophy(next);
        if (flags.SOUND) playAchievement();
        scheduleTimeout(() => {
            setTrophy(null);
            scheduleTimeout(() => {
                trophyBusyRef.current = false;
                showNextTrophy();
            }, ACHIEVEMENT.STAGGER_MS);
        }, ACHIEVEMENT.POPUP_MS);
    };
    const enqueueTrophy = (payload) => {
        if (trophyQueueRef.current.length >= ACHIEVEMENT.MAX_QUEUE) return;
        trophyQueueRef.current.push(payload);
        showNextTrophy();
    };

    // Per-name daily stats — for achievement detection.
    // Keyed by `${colKey}::${name}` because a person can achieve independently in each column.
    const statsRef = useRef({}); // { key: { firstSeenAt, overtakeTimes: [ts,...], worstRank, bestRank, everCentury, everHatTrick, everComeback, hasLedAllDay: bool, firstLeaderTs: ts } }
    // Compute today's calendar day IN the location's timezone. Board rolls
    // over at midnight LOCAL — a Las Vegas TV shouldn't wipe scores at
    // 9 PM local (midnight Eastern) just because the browser is set to ET.
    const dayInLocationTZ = (d, tz) => {
        try {
            return new Intl.DateTimeFormat('en-CA', {
                timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit',
            }).format(d);
        } catch {
            return d.toISOString().slice(0, 10);
        }
    };
    const dayResetGuardRef = useRef(
        dayInLocationTZ(new Date(), locationTZ(null))   // seed w/ default; updates on first data
    );

    // Reset at midnight LOCAL. When the location's calendar date changes,
    // clear all stats + feed + achievements so the board mirrors the
    // server's daily reset without needing a manual reload.
    useEffect(() => {
        const tz = locationTZ(data?.location_name);
        const today = dayInLocationTZ(clockNow, tz);
        if (today !== dayResetGuardRef.current) {
            dayResetGuardRef.current = today;
            statsRef.current = {};
            trophyQueueRef.current = [];
            earnedRef.current = {};
            setKillFeed([]);
        }
    }, [clockNow, data?.location_name]);

    const earnedRef = useRef({}); // { key: Set<achievementId> }
    const alreadyEarned = (key, achId) => {
        const s = earnedRef.current[key];
        return !!(s && s.has(achId));
    };
    const markEarned = (key, achId) => {
        (earnedRef.current[key] = earnedRef.current[key] || new Set()).add(achId);
    };

    // ─── Machine tier tracker ────────────────────────────────────
    // Tier index: 0=none, 1=HALF, 2=THE, 3=MAX, 4=MEGA, 5=ULTRA SUPER, 6=GOD MODE
    // When someone crosses UPWARD, play the corresponding MP3.
    const machineTierRef = useRef({});
    const machineTierFor = (colKey, score) => {
        if (score >= (TITLES.GOD_MODE_MIN_SCORE[colKey]            ?? Infinity)) return 6;
        if (score >= (TITLES.ULTRA_SUPER_MACHINE_MIN_SCORE[colKey] ?? Infinity)) return 5;
        if (score >= (TITLES.MEGA_MACHINE_MIN_SCORE[colKey]        ?? Infinity)) return 4;
        if (score >= (TITLES.MAX_MACHINE_MIN_SCORE[colKey]         ?? Infinity)) return 3;
        if (score >= (TITLES.MACHINE_MIN_SCORE[colKey]             ?? Infinity)) return 2;
        if (score >= (TITLES.HALF_MACHINE_MIN_SCORE[colKey]        ?? Infinity)) return 1;
        return 0;
    };
    const detectMachineTierUps = (newData) => {
        if (!flags.SOUND) return;
        for (const col of COLUMNS) {
            for (const r of newData.columns[col.key] || []) {
                const key = `${col.key}::${r.name}`;
                const newTier = machineTierFor(col.key, r.score);
                const prevTier = machineTierRef.current[key];
                if (prevTier != null && newTier > prevTier) {
                    playMachineTier(newTier);
                    jumpToUser(col.key, r.name);
                    // Ultimate rung — layered sound + fullscreen confetti + roar
                    if (newTier === 6) {
                        synth.roar();
                        confetti({
                            particleCount: 700, spread: 180, startVelocity: 60,
                            origin: { x: 0.5, y: 0.55 },
                        });
                    } else if (newTier === 5) {
                        confetti({
                            particleCount: 250, spread: 140, startVelocity: 50,
                            origin: { x: 0.5, y: 0.6 },
                        });
                    }
                }
                machineTierRef.current[key] = newTier;
            }
        }
    };

    // ─── Level tracker: fire level-up flash when someone crosses a level ─
    const levelsRef = useRef({}); // { key: currentLevel }
    const [levelFlashKey, setLevelFlashKey] = useState(null);
    const pointsPerLevel       = (colKey) => LEVEL.POINTS_PER_LEVEL[colKey] || 100;
    const halfMachineMin       = (colKey) => TITLES.HALF_MACHINE_MIN_SCORE[colKey] || 400;
    const machineMin           = (colKey) => TITLES.MACHINE_MIN_SCORE[colKey] || 800;
    const maxMachineMin        = (colKey) => TITLES.MAX_MACHINE_MIN_SCORE[colKey] || 1200;
    const megaMachineMin       = (colKey) => TITLES.MEGA_MACHINE_MIN_SCORE[colKey] || 1600;
    const ultraSuperMachineMin = (colKey) => TITLES.ULTRA_SUPER_MACHINE_MIN_SCORE[colKey] || 2000;
    const godModeMin           = (colKey) => TITLES.GOD_MODE_MIN_SCORE[colKey] || 3000;
    const detectLevelUps = (newData) => {
        for (const col of COLUMNS) {
            for (const r of newData.columns[col.key] || []) {
                const key = `${col.key}::${r.name}`;
                const nowLv = Math.floor(r.score / pointsPerLevel(col.key));
                const prevLv = levelsRef.current[key];
                if (prevLv != null && nowLv > prevLv) {
                    setLevelFlashKey(key);
                    scheduleTimeout(() => setLevelFlashKey(prev => prev === key ? null : prev), LEVEL.FLASH_MS);
                    if (flags.SOUND) playLevelUp();
                    jumpToUser(col.key, r.name);
                }
                levelsRef.current[key] = nowLv;
            }
        }
    };

    // ─── Board-wide milestone tracker: sum ALL scores across all 3 columns.
    // Two tiers:
    //   REGULAR — quick takeover, roar.
    //   GRAND   — bigger overlay + confetti + trophy chime + roar.
    // First-mount guard so a mid-day page load doesn't fire a backlog of already-crossed thresholds.
    const milestoneCrossedRef = useRef(new Set());
    const milestoneMountedRef = useRef(false);
    const [milestoneShown, setMilestoneShown] = useState(null);
    const detectMilestones = (newData) => {
        if (!flags.MILESTONE || MILESTONE.ENABLED === false) return;
        let total = 0;
        for (const col of COLUMNS) {
            for (const r of newData.columns[col.key] || []) total += r.score;
        }
        // On first poll: mark everything already achieved as silently crossed
        // so we only celebrate NEW crossings from here on.
        if (!milestoneMountedRef.current && MILESTONE.SUPPRESS_ON_MOUNT) {
            milestoneMountedRef.current = true;
            for (const t of MILESTONE.ALL_SORTED) {
                if (total >= t) milestoneCrossedRef.current.add(t);
                else break;
            }
            return;
        }
        milestoneMountedRef.current = true;
        for (const t of MILESTONE.ALL_SORTED) {
            if (total >= t && !milestoneCrossedRef.current.has(t)) {
                milestoneCrossedRef.current.add(t);
                const isGrand = MILESTONE.IS_GRAND.has(t);
                setMilestoneShown({
                    id: `milestone-${t}`,
                    threshold: t,
                    total: Math.round(total),
                    grand: isGrand,
                });
                if (flags.SOUND) {
                    synth.roar();       // deep low-end hit for the takeover
                    playMilestone();    // celebratory milestone sound
                }
                if (isGrand) {
                    // Fullscreen confetti burst for grand milestones.
                    confetti({
                        particleCount: 500, spread: 160, startVelocity: 55,
                        origin: { x: 0.5, y: 0.5 },
                    });
                }
                scheduleTimeout(
                    () => setMilestoneShown(prev => (prev && prev.id === `milestone-${t}`) ? null : prev),
                    isGrand ? MILESTONE.GRAND_HOLD_MS : MILESTONE.REGULAR_HOLD_MS,
                );
                break;
            }
        }
    };

    // ─── Random spotlight: every 20-30 min pick a top performer and
    //     do a camera-zoom takeover for a few seconds.
    const [spotlight, setSpotlight] = useState(null);
    const spotlightScheduleRef = useRef(null);
    const scheduleNextSpotlight = () => {
        if (!flags.SPOTLIGHT || SPOTLIGHT.ENABLED === false) return;
        if (spotlightScheduleRef.current != null) clearTimeout(spotlightScheduleRef.current);
        const delay = SPOTLIGHT.MIN_INTERVAL_MS + Math.random() * (SPOTLIGHT.MAX_INTERVAL_MS - SPOTLIGHT.MIN_INTERVAL_MS);
        spotlightScheduleRef.current = setTimeout(() => {
            if (Date.now() >= animBusyUntilRef.current
                && !milestoneShown
                && !showcaseVisible
                && data) {
                // Pick a random column-top-3 person
                const pool = [];
                for (const col of COLUMNS) {
                    for (const r of (data.columns[col.key] || []).slice(0, 5)) {
                        pool.push({ colKey: col.key, colLabel: col.label, ...r });
                    }
                }
                if (pool.length) {
                    const pick = pool[Math.floor(Math.random() * pool.length)];
                    setSpotlight(pick);
                    if (flags.SOUND) synth.whoosh();
                    scheduleTimeout(() => setSpotlight(null), SPOTLIGHT.HOLD_MS);
                }
            }
            scheduleNextSpotlight();
        }, delay);
    };
    useEffect(() => {
        scheduleNextSpotlight();
        return () => { if (spotlightScheduleRef.current) clearTimeout(spotlightScheduleRef.current); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ─── Bonus flavor callouts ──────────────────────────────────────
    // Fire on board STATES, not just rank changes:
    //   CLOSE RACE — top 2 within CLOSE_RACE_MAX_GAP pts
    //   SHUTOUT    — leader has SHUTOUT_MULT× second place's score
    //   TIED       — two people share a top rank
    //   SECOND WIND — score gain after 5+ minutes of nothing
    // Each has a per-column, per-kind cooldown so a persistent state
    // doesn't spam the screen.
    const flavorCooldownRef = useRef({});   // { "col::kind": nextEligibleTs }
    const lastScoreChangeRef = useRef({});  // { "col::name": ts } for SECOND WIND
    const flavorEligible = (col, kind) => {
        const key = `${col}::${kind}`;
        const now = Date.now();
        if ((flavorCooldownRef.current[key] || 0) > now) return false;
        flavorCooldownRef.current[key] = now + FLAVOR.COOLDOWN_MS;
        return true;
    };
    const detectFlavor = (newData) => {
        if (!FLAVOR.ENABLED) return;
        const now = Date.now();
        for (const col of COLUMNS) {
            const rows = newData.columns[col.key] || [];
            if (rows.length < 2) continue;
            const [first, second] = rows;
            const gap = first.score - second.score;
            // CLOSE RACE — top 2 within gap threshold, both > 0
            if (first.score > 0 && gap > 0 && gap <= FLAVOR.CLOSE_RACE_MAX_GAP) {
                if (flavorEligible(col.key, 'close_race')) {
                    enqueueCallouts([{ colKey: col.key, name: `${first.name} vs ${second.name}`,
                                        delta: 0, newRank: 0, _flavor: 'close_race' }]);
                }
            }
            // SHUTOUT — leader >= SHUTOUT_MULT × second, both > 0
            if (second.score > 0 && first.score >= FLAVOR.SHUTOUT_MULT * second.score) {
                if (flavorEligible(col.key, 'shutout')) {
                    enqueueCallouts([{ colKey: col.key, name: first.name, delta: 0, newRank: 1, _flavor: 'shutout' }]);
                }
            }
            // TIED — same score at a top rank
            for (let i = 0; i < Math.min(rows.length - 1, FLAVOR.TIED_MIN_RANK); i++) {
                if (rows[i].score === rows[i + 1].score && rows[i].score > 0) {
                    if (flavorEligible(col.key, `tied_${i}`)) {
                        enqueueCallouts([{ colKey: col.key, name: `${rows[i].name} = ${rows[i+1].name}`,
                                            delta: 0, newRank: rows[i].rank, _flavor: 'tied' }]);
                    }
                    break; // one tie announcement per column per poll
                }
            }
            // SECOND WIND — someone whose last score change was 5+ min ago just bumped
            const prevRows = prevSnapshotRef.current[col.key];
            if (prevRows) {
                for (const r of rows) {
                    const prevScore = prevRows[r.name];
                    if (prevScore == null) continue;
                    const scoreKey = `${col.key}::${r.name}`;
                    const lastChange = lastScoreChangeRef.current[scoreKey] || 0;
                    if (r.score > prevScore) {
                        if (lastChange && (now - lastChange) >= FLAVOR.QUIET_MIN_MS
                            && flavorEligible(col.key, `wind_${r.name}`)) {
                            enqueueCallouts([{ colKey: col.key, name: r.name,
                                                delta: 0, newRank: r.rank, _flavor: 'second_wind' }]);
                        }
                        lastScoreChangeRef.current[scoreKey] = now;
                    }
                }
            }
        }
    };

    // ─── Rivalry tracker ────────────────────────────────────────────
    // For each column, count how many times any two people have swapped
    // relative rank. Once a pair crosses SWAP_THRESHOLD → rivals.
    // When rivals are within BATTLE_DIFF points → BATTLE cut-in (cooldown-gated).
    const rivalryRef = useRef({}); // { colKey: { "A||B": { count, lastSwapTs, lastBattleTs } } }
    const [battle, setBattle] = useState(null);
    const battleBusyRef = useRef(false);
    const pairKey = (a, b) => (a < b ? `${a}||${b}` : `${b}||${a}`);
    const detectRivalriesAndBattles = (newData) => {
        if (!flags.RIVALRIES || RIVALRY.ENABLED === false) return;
        const prev = prevSnapshotRef.current;
        const now = Date.now();
        for (const col of COLUMNS) {
            const rows = newData.columns[col.key] || [];
            const prevRanks = prev[col.key] || {};
            const pairs = rivalryRef.current[col.key] = rivalryRef.current[col.key] || {};
            // For each pair in the current top 15, detect a swap since last poll.
            for (let i = 0; i < rows.length; i++) {
                for (let j = i + 1; j < rows.length; j++) {
                    const a = rows[i].name, b = rows[j].name;
                    const pa = prevRanks[a], pb = prevRanks[b];
                    if (pa == null || pb == null) continue;
                    // Same relative order today? Skip.
                    const orderPrev = pa < pb ? 'a_ahead' : 'b_ahead';
                    const orderNow  = rows[i].rank < rows[j].rank ? 'a_ahead' : 'b_ahead';
                    if (orderPrev === orderNow) continue;
                    const k = pairKey(a, b);
                    const entry = pairs[k] = pairs[k] || { count: 0, lastSwapTs: 0, lastBattleTs: 0 };
                    entry.count++;
                    entry.lastSwapTs = now;
                    // Crossed into RIVALS threshold this poll? Fire a callout.
                    if (entry.count === RIVALRY.SWAP_THRESHOLD) {
                        enqueueTrophy({
                            id: `${col.key}-rivalry-${k}-${now}`,
                            name: `${a} vs ${b}`,
                            label: 'Rivals',
                            tone: 'red',
                            sub: `${RIVALRY.SWAP_THRESHOLD}+ swaps in ${col.label}`,
                        });
                    }
                    // BATTLE eligibility: at rival threshold + close scores + cooldown clear
                    if (entry.count >= RIVALRY.SWAP_THRESHOLD
                        && Math.abs(rows[i].score - rows[j].score) <= RIVALRY.BATTLE_DIFF
                        && (now - entry.lastBattleTs) >= RIVALRY.BATTLE_COOLDOWN_MS
                        && !battleBusyRef.current
                        && !milestoneShown
                        && !spotlight
                        && !showcaseVisible) {
                        entry.lastBattleTs = now;
                        battleBusyRef.current = true;
                        const p1 = rows[i].score >= rows[j].score ? rows[i] : rows[j];
                        const p2 = rows[i].score >= rows[j].score ? rows[j] : rows[i];
                        setBattle({
                            id: `battle-${k}-${now}`, colLabel: col.label,
                            leader: p1, chaser: p2, swaps: entry.count,
                        });
                        if (flags.SOUND) playBattle();
                        scheduleTimeout(() => {
                            setBattle(null);
                            scheduleTimeout(() => { battleBusyRef.current = false; }, 500);
                        }, RIVALRY.BATTLE_HOLD_MS);
                    }
                }
            }
        }
    };

    // ─── Dynamic day narrative — rotating context line under the title.
    // Regenerates from board state every NARRATIVE.ROTATE_MS.
    const leaderHoldRef = useRef({}); // { colKey: { name, since_ts } }
    const leadChangesRef = useRef({ pickers: 0, pressers: 0, packers: 0 });
    const trackLeaderHold = (newData) => {
        const now = Date.now();
        for (const col of COLUMNS) {
            const leader = (newData.columns[col.key] || [])[0]?.name || null;
            const held = leaderHoldRef.current[col.key];
            if (!held || held.name !== leader) {
                if (held) leadChangesRef.current[col.key]++;
                leaderHoldRef.current[col.key] = { name: leader, since: now };
            }
        }
    };
    const [narrativeIdx, setNarrativeIdx] = useState(0);
    useEffect(() => {
        if (!flags.NARRATIVE) return;
        const id = setInterval(() => setNarrativeIdx(i => (i + 1) % 6), NARRATIVE.ROTATE_MS);
        return () => clearInterval(id);
    }, [flags.NARRATIVE]);
    const composeNarrative = () => {
        if (!data) return '';
        const lines = [];
        for (const col of COLUMNS) {
            const held = leaderHoldRef.current[col.key];
            if (held && held.name) {
                const mins = Math.round((Date.now() - held.since) / 60_000);
                if (mins >= 5) lines.push(`${held.name} has led ${col.label} for ${mins} min`);
            }
            const changes = leadChangesRef.current[col.key];
            if (changes >= 3) lines.push(`${changes} lead changes in ${col.label} today`);
        }
        // Add hottest current streak
        let hottest = null;
        for (const col of COLUMNS) {
            for (const r of (data.columns[col.key] || [])) {
                const s = streakForRow(col.key, r.name);
                if (s && (!hottest || STREAK.LEVELS.indexOf(s) > STREAK.LEVELS.indexOf(hottest.s))) {
                    hottest = { name: r.name, colLabel: col.label, s };
                }
            }
        }
        if (hottest) lines.push(`${hottest.name} is ${hottest.s.label} in ${hottest.colLabel}`);
        if (!lines.length) return 'The board is warming up.';
        return lines[narrativeIdx % lines.length];
    };

    // Live clock in header
    useEffect(() => {
        const id = setInterval(() => setClockNow(new Date()), 1000);
        return () => clearInterval(id);
    }, []);

    // Periodic showcase — fires on the interval, but only when the board isn't
    // busy animating a rank change (don't cover the drama).
    useEffect(() => {
        if (SHOWCASE.DISABLED || noshowcase) return;
        const id = setInterval(() => {
            if (Date.now() >= animBusyUntilRef.current) setShowcaseVisible(true);
        }, SHOWCASE.INTERVAL_MIN * 60_000);
        return () => clearInterval(id);
    }, [noshowcase]);

    // Ambient arena hum — one lifecycle for the whole page.
    useEffect(() => {
        if (flags.AMBIENT) synth.startAmbient();
        else synth.stopAmbient();
        return () => synth.stopAmbient();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [flags.AMBIENT]);

    // ─── Anim scheduler helpers ─────────────────────────────────────
    const setPhase = (colKey, name, phase) => {
        setPhases(prev => {
            const next = { ...prev };
            const k = `${colKey}::${name}`;
            if (phase == null) delete next[k];
            else next[k] = phase;
            return next;
        });
    };

    const scheduleTimeout = (fn, ms) => {
        const id = setTimeout(fn, ms);
        scheduledTimeoutsRef.current.push(id);
    };

    const clearScheduledTimeouts = () => {
        scheduledTimeoutsRef.current.forEach(clearTimeout);
        scheduledTimeoutsRef.current = [];
    };

    // ─── Score history + streak helpers ─────────────────────────────
    // Track prior streak level per person so we fire sizzle exactly once when
    // they cross into ON FIRE / BLAZING (not every poll they're hot).
    const streakLevelRef = useRef({});
    const updateScoreHistory = (nextData) => {
        const now = Date.now();
        const cutoff = now - STREAK.STALE_AFTER_MS;
        const hist = scoreHistoryRef.current;
        for (const col of COLUMNS) {
            for (const r of nextData.columns[col.key] || []) {
                const key = `${col.key}::${r.name}`;
                const arr = hist[key] || [];
                const last = arr[arr.length - 1];
                const scoreIncreased = last && r.score > last.score;
                if (!last || last.score !== r.score) {
                    arr.push({ score: r.score, ts: now });
                    if (scoreIncreased && flags.TICK) playTick();
                }
                while (arr.length && arr[0].ts < cutoff) arr.shift();
                hist[key] = arr;
            }
        }
    };

    const streakForRow = (colKey, name) => {
        const arr = scoreHistoryRef.current[`${colKey}::${name}`] || [];
        if (arr.length < 2) return null;
        const now = Date.now();
        const last = arr[arr.length - 1];
        if (now - last.ts > STREAK.STALE_AFTER_MS) return null;
        const windowStart = now - STREAK.WINDOW_MS;
        // Find the first entry within the window (or use the earliest we have)
        let firstIn = arr.find(e => e.ts >= windowStart) || arr[0];
        const dtMin = (last.ts - firstIn.ts) / 60_000;
        if (dtMin <= 0) return null;
        const pace = (last.score - firstIn.score) / dtMin; // points/min
        let level = null;
        for (const l of STREAK.LEVELS) {
            if (pace >= l.min) level = l;
        }
        return level;
    };

    // ─── Callout queue helpers ──────────────────────────────────────
    const enqueueCallouts = (movers) => {
        // movers: [{ colKey, name, delta, newRank }]
        // Build callout payloads and per-column enqueue with stagger.
        const byCol = { pickers: [], pressers: [], packers: [] };
        movers.forEach(m => {
            let kind = 'move', label = 'ON THE MOVE';
            if (m.newRank === 1) { kind = 'leader'; label = 'NEW LEADER'; }
            else if (m.newRank <= CALLOUT.THRESHOLDS.PODIUM) { kind = 'podium'; label = 'PODIUM'; }
            else if (m.delta >= CALLOUT.THRESHOLDS.HUGE) { kind = 'huge'; label = 'STORMING UP'; }
            else if (m.delta >= CALLOUT.THRESHOLDS.BIG) { kind = 'big'; label = `+${m.delta} POSITIONS`; }
            else { kind = 'move'; label = 'ON THE MOVE'; }
            byCol[m.colKey].push({
                id: `${m.colKey}-${m.name}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
                colKey: m.colKey,
                kind,
                label,
                name: m.name,
                sub: kind !== 'move' && kind !== 'big' ? `#${m.newRank}` : (m.delta > 1 ? `+${m.delta}` : null),
            });
        });
        setCalloutsByCol(prev => {
            const next = { ...prev };
            for (const col of COLUMNS) {
                const merged = [...(prev[col.key] || []), ...byCol[col.key]];
                next[col.key] = merged.slice(-CALLOUT.MAX_PER_COL);
            }
            return next;
        });
        // Dismiss head-of-queue callouts on a stagger.
        for (const col of COLUMNS) {
            byCol[col.key].forEach((p, i) => {
                const delay = i * CALLOUT.STAGGER_MS + CALLOUT.DURATION_MS;
                scheduleTimeout(() => {
                    setCalloutsByCol(prev => {
                        const cur = prev[col.key] || [];
                        return { ...prev, [col.key]: cur.filter(x => x.id !== p.id) };
                    });
                }, delay);
            });
        }
    };

    // Compute rank movers between prev and next snapshots.
    // Returns { up: [{colKey, name, delta}], down: [...], leaderChange: [colKey, ...] }
    const diffRanks = (prevSnap, nextData) => {
        const up = [], down = [], leaderChange = [];
        COLUMNS.forEach(col => {
            const newRows = nextData?.columns?.[col.key] || [];
            const prev = prevSnap[col.key] || {};
            // Detect NEW #1 vs OLD #1 by name
            const prevLeader = Object.entries(prev).find(([, r]) => r === 1)?.[0];
            const newLeader = newRows[0]?.name;
            if (prevLeader && newLeader && prevLeader !== newLeader) {
                leaderChange.push(col.key);
            }
            newRows.forEach(r => {
                const wasAt = prev[r.name];
                if (wasAt != null && wasAt > r.rank) {
                    up.push({ colKey: col.key, name: r.name, delta: wasAt - r.rank });
                } else if (wasAt != null && wasAt < r.rank) {
                    down.push({ colKey: col.key, name: r.name, delta: wasAt - r.rank });
                }
            });
        });
        // Movers-up sorted biggest jump first for prioritization
        up.sort((a, b) => b.delta - a.delta);
        return { up, down, leaderChange };
    };

    // ─── Auto-scroll rAF loop ───────────────────────────────────────
    // Translates the inner .board-col__track by translateY(-pos) so we
    // bypass scrollTop (which was being ignored — probably because
    // framer-motion's LayoutGroup owns the row transforms).
    // Continuous smooth back-and-forth. Only runs when track height > list
    // viewport. jumpToUser can pause a column for JUMP_HOLD_MS.
    useEffect(() => {
        if (!AUTO_SCROLL.ENABLED) return;
        let raf = 0;
        let running = true;
        const step = () => {
            if (!running) return;
            const now = Date.now();
            for (const col of COLUMNS) {
                const state = scrollStateRef.current[col.key];
                const listEl = listRefs[col.key].current;
                const trackEl = trackRefs[col.key].current;
                if (!listEl || !trackEl || !state) continue;
                if (now < state.pauseUntil) continue;
                const listH  = listEl.clientHeight;
                // Measure REAL content height only — ghost rows fill the
                // column visually but shouldn't trigger auto-scroll.
                const realRows = trackEl.querySelectorAll('.board-row:not(.board-row--ghost)');
                if (realRows.length === 0) continue;
                const lastReal = realRows[realRows.length - 1];
                const realContentH = lastReal.offsetTop + lastReal.offsetHeight;
                const max = realContentH - listH;
                if (max <= 1) {
                    state.pos = 0;
                    trackEl.style.transform = 'translate3d(0, 0, 0)';
                    continue;
                }
                state.pos += state.dir * AUTO_SCROLL.PX_PER_FRAME;
                if (state.pos >= max) {
                    state.pos = max;
                    state.dir = -1;
                    if (AUTO_SCROLL.BOTTOM_PAUSE_MS > 0) state.pauseUntil = now + AUTO_SCROLL.BOTTOM_PAUSE_MS;
                } else if (state.pos <= 0) {
                    state.pos = 0;
                    state.dir = 1;
                    if (AUTO_SCROLL.TOP_PAUSE_MS > 0) state.pauseUntil = now + AUTO_SCROLL.TOP_PAUSE_MS;
                }
                trackEl.style.transform = `translate3d(0, ${-state.pos}px, 0)`;
            }
            raf = requestAnimationFrame(step);
        };
        raf = requestAnimationFrame(step);
        const vis = () => { running = document.visibilityState === 'visible'; if (running) raf = requestAnimationFrame(step); };
        document.addEventListener('visibilitychange', vis);
        return () => {
            running = false;
            cancelAnimationFrame(raf);
            document.removeEventListener('visibilitychange', vis);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ─── jumpToUser — center a row and pause auto-scroll ────────────
    // Called when something happens to a user (rank up, level up, streak
    // crossed, achievement, machine tier). Pauses that column's auto-scroll
    // for AUTO_SCROLL.JUMP_HOLD_MS so viewers can see their name.
    const jumpToUser = (colKey, name) => {
        if (!AUTO_SCROLL.ENABLED || !AUTO_SCROLL.JUMP_ON_EVENTS) return;
        const listEl  = listRefs[colKey]?.current;
        const trackEl = trackRefs[colKey]?.current;
        if (!listEl || !trackEl) return;
        // Find the target row in the inner track by its name.
        const rows = trackEl.querySelectorAll('.board-row');
        let targetRow = null;
        for (const row of rows) {
            const label = row.querySelector('.board-name__label');
            if (label && label.textContent === name) { targetRow = row; break; }
        }
        if (!targetRow) return;
        // Row's offsetTop is relative to the TRACK's untranslated origin.
        const rowOffsetTop = targetRow.offsetTop;
        const rowHeight = targetRow.offsetHeight;
        const listH = listEl.clientHeight;
        // Center the row in the viewport
        let target = rowOffsetTop - (listH / 2) + (rowHeight / 2);
        const trackH = trackEl.scrollHeight;
        const max = Math.max(0, trackH - listH);
        target = Math.max(0, Math.min(max, target));
        // Smooth CSS transition to the target, then hand back to the rAF loop.
        trackEl.style.transition = 'transform 500ms ease-out';
        trackEl.style.transform = `translate3d(0, ${-target}px, 0)`;
        const state = scrollStateRef.current[colKey];
        if (state) {
            state.pos = target;
            state.pauseUntil = Date.now() + AUTO_SCROLL.JUMP_HOLD_MS;
            // Preserve the existing scroll direction — the jump-to-user
            // shouldn't reverse the column. If the jump landed at either
            // end, the rAF loop's clamp will flip direction naturally on
            // the next frame past end.
        }
        // Clear the transition after the animation so rAF-driven per-frame
        // updates don't fight it.
        scheduleTimeout(() => {
            if (trackEl) trackEl.style.transition = 'none';
        }, 520);
    };

    // Compute an earned title for one row based on stats + streak.
    // Checked in descending priority — highest-tier earned title wins.
    const titleFor = (colKey, row) => {
        if (!flags.TITLES) return null;
        const key = `${colKey}::${row.name}`;
        const streak = streakForRow(colKey, row.name);
        const streakIdx = streak ? STREAK.LEVELS.findIndex(l => l.label === streak.label) : -1;
        if (row.score >= godModeMin(colKey))           return 'GOD MODE';
        if (row.score >= ultraSuperMachineMin(colKey)) return 'ULTRA SUPER MACHINE';
        if (streakIdx >= TITLES.BLAZING_LEVEL_IDX)     return 'SPEED DEMON';
        if (row.score >= megaMachineMin(colKey))       return 'MEGA MACHINE';
        if (row.score >= maxMachineMin(colKey))        return 'MAX MACHINE';
        if (row.score >= machineMin(colKey))           return 'THE MACHINE';
        if (row.score >= halfMachineMin(colKey))       return 'HALF MACHINE';
        if (alreadyEarned(key, 'hat_trick')) return 'CLUTCH';
        if (alreadyEarned(key, 'comeback')) return 'COMEBACK KID';
        if (alreadyEarned(key, 'century')) return 'CENTURION';
        return null;
    };

    // Run the full dramatic sequence for one poll's changes.
    const runSequence = (newData) => {
        // Update score history FIRST so streak reads reflect the new data.
        updateScoreHistory(newData);
        detectLevelUps(newData);
        detectMachineTierUps(newData);
        trackLeaderHold(newData);
        detectMilestones(newData);
        detectRivalriesAndBattles(newData);
        const prevSnap = prevSnapshotRef.current;
        const { up, down, leaderChange } = diffRanks(prevSnap, newData);
        const highlighted = up.slice(0, ANIM.MAX_HIGHLIGHTED);

        // Fire broadcast callouts for every mover-up (not just highlighted).
        if (up.length > 0) {
            enqueueCallouts(up.map(m => ({
                colKey: m.colKey, name: m.name, delta: m.delta,
                newRank: (newData.columns[m.colKey] || []).find(r => r.name === m.name)?.rank || 0,
            })));
        }

        // ─── Kill feed: log every rank change + leader change ───────
        const colLabel = { pickers: 'Pickers', pressers: 'Pressers', packers: 'Packers' };
        if (flags.KILL_FEED) {
            up.forEach(m => {
                const newRank = (newData.columns[m.colKey] || []).find(r => r.name === m.name)?.rank || 0;
                if (newRank === 1) pushFeed('leader', `${m.name} takes the ${colLabel[m.colKey]} lead`);
                else if (m.delta >= 3) pushFeed('storm', `${m.name} storms up ${m.delta} spots in ${colLabel[m.colKey]}`);
                else pushFeed('overtake', `${m.name} overtakes into #${newRank} in ${colLabel[m.colKey]}`);
            });
        }

        // ─── Arcade sound triggers ─────────────────────────────────
        if (flags.SOUND) {
            if (leaderChange.length > 0 && flags.ROAR) synth.roar();
            // Overtake sounds — 4-tier: podium / storming up / big / on the move
            if (flags.WHOOSH) {
                for (const m of up) {
                    const newRank = (newData.columns[m.colKey] || [])
                        .find(r => r.name === m.name)?.rank || 99;
                    if (newRank <= CALLOUT.THRESHOLDS.PODIUM)     playPodium();
                    else if (m.delta >= CALLOUT.THRESHOLDS.HUGE) playStormingUp();
                    else if (m.delta >= CALLOUT.THRESHOLDS.BIG)  playBigOvertake();
                    else                                         playOvertake();
                }
            }
            // Auto-scroll: jump each column to the first mover so viewers
            // can see who's on the move. Multiple movers → last one wins,
            // fine for now.
            for (const m of up) jumpToUser(m.colKey, m.name);
        }

        // ─── Sizzle on streak-level crossings ──────────────────────
        for (const col of COLUMNS) {
            for (const r of (newData.columns[col.key] || [])) {
                const key = `${col.key}::${r.name}`;
                const level = streakForRow(col.key, r.name);
                const prevLevelIdx = streakLevelRef.current[key] ?? -1;
                const newLevelIdx = level ? STREAK.LEVELS.findIndex(l => l.label === level.label) : -1;
                if (newLevelIdx > prevLevelIdx && newLevelIdx >= 1) {
                    if (flags.SIZZLE) playStreakForLevel(newLevelIdx + 1);
                    if (flags.KILL_FEED) {
                        pushFeed('streak', `${r.name} is ${level.label} — ${colLabel[col.key]}`);
                    }
                    jumpToUser(col.key, r.name);
                }
                streakLevelRef.current[key] = newLevelIdx;
            }
        }

        // ─── Achievement detection ─────────────────────────────────
        if (flags.ACHIEVEMENTS) {
            const now = Date.now();
            for (const col of COLUMNS) {
                const rows = newData.columns[col.key] || [];
                for (const r of rows) {
                    const key = `${col.key}::${r.name}`;
                    const s = (statsRef.current[key] = statsRef.current[key] || {
                        firstSeenAt: now, overtakeTimes: [], worstRank: r.rank, bestRank: r.rank,
                    });
                    s.worstRank = Math.max(s.worstRank, r.rank);
                    s.bestRank = Math.min(s.bestRank, r.rank);
                    // Century Club
                    if (r.score >= ACHIEVEMENT.CENTURY_MIN && !alreadyEarned(key, 'century')) {
                        markEarned(key, 'century'); jumpToUser(col.key, r.name);
                        enqueueTrophy({
                            id: `${key}-century-${now}`, name: r.name,
                            label: 'Century Club', tone: 'gold',
                            sub: `${colLabel[col.key]} · ${ACHIEVEMENT.CENTURY_MIN}+ points today`,
                        });
                        if (flags.KILL_FEED) pushFeed('trophy', `${r.name} joins the Century Club (${colLabel[col.key]})`);
                    }
                }
                // Hat trick — 3 overtakes within HAT_TRICK_WINDOW_MS
                for (const m of up) {
                    if (m.colKey !== col.key) continue;
                    const key = `${col.key}::${m.name}`;
                    const s = statsRef.current[key];
                    if (!s) continue;
                    s.overtakeTimes.push(now);
                    while (s.overtakeTimes.length && (now - s.overtakeTimes[0]) > ACHIEVEMENT.HAT_TRICK_WINDOW_MS) {
                        s.overtakeTimes.shift();
                    }
                    if (s.overtakeTimes.length >= ACHIEVEMENT.HAT_TRICK_COUNT && !alreadyEarned(key, 'hat_trick')) {
                        markEarned(key, 'hat_trick'); jumpToUser(m.colKey, m.name);
                        enqueueTrophy({
                            id: `${key}-hat-${now}`, name: m.name,
                            label: 'Hat Trick', tone: 'red',
                            sub: `${ACHIEVEMENT.HAT_TRICK_COUNT} overtakes in an hour · ${colLabel[m.colKey]}`,
                        });
                        if (flags.KILL_FEED) pushFeed('trophy', `${m.name} lands the Hat Trick (${colLabel[m.colKey]})`);
                    }
                    // Comeback Kid — from ACHIEVEMENT.COMEBACK_FROM+ to ACHIEVEMENT.COMEBACK_TO or better
                    const newRank = (newData.columns[m.colKey] || []).find(r => r.name === m.name)?.rank || 99;
                    if (s.worstRank >= ACHIEVEMENT.COMEBACK_FROM
                        && newRank <= ACHIEVEMENT.COMEBACK_TO
                        && !alreadyEarned(key, 'comeback')) {
                        markEarned(key, 'comeback'); jumpToUser(m.colKey, m.name);
                        enqueueTrophy({
                            id: `${key}-cb-${now}`, name: m.name,
                            label: 'Comeback Kid', tone: 'cyan',
                            sub: `Bottom ${ACHIEVEMENT.COMEBACK_FROM} → Top ${ACHIEVEMENT.COMEBACK_TO} · ${colLabel[m.colKey]}`,
                        });
                        if (flags.KILL_FEED) pushFeed('trophy', `${m.name} pulls off the Comeback Kid (${colLabel[m.colKey]})`);
                    }
                }
            }
        }

        if (highlighted.length === 0 && down.length === 0 && leaderChange.length === 0) {
            snapshotAndApply(newData);
            return;
        }

        // 1) Attention phase — staggered shake+scale on each highlighted mover-up
        highlighted.forEach((m, i) => {
            scheduleTimeout(() => setPhase(m.colKey, m.name, 'attention'), i * ANIM.STAGGER_MS);
        });

        // 2) After ALL attention phases have started AND held ATTENTION_MS,
        //    swap the data → Framer layout animation slides everything.
        //    Set moving-phase on highlighted, drop-phase on movers-down (subdued red).
        const dataSwapAt = (highlighted.length - 1) * ANIM.STAGGER_MS + ANIM.ATTENTION_MS;
        scheduleTimeout(() => {
            highlighted.forEach(m => setPhase(m.colKey, m.name, 'moving'));
            down.forEach(m => setPhase(m.colKey, m.name, 'drop'));
            snapshotAndApply(newData);
            // Confetti + chime happen at the SAME beat as the leader row lands center-stage
            leaderChange.forEach(colKey => {
                const cols = document.querySelectorAll('.board-col');
                const idx = COLUMNS.findIndex(c => c.key === colKey);
                const rect = cols[idx]?.getBoundingClientRect();
                if (rect) {
                    confetti({
                        particleCount: 300, spread: 100, startVelocity: 55,
                        origin: {
                            x: (rect.left + rect.width / 2) / window.innerWidth,
                            y: (rect.top + rect.height / 2) / window.innerHeight,
                        },
                    });
                }
                if (!muted) playChime();
            });
        }, dataSwapAt);

        // 3) After MOVE_MS, landing bounce for highlighted movers-up
        scheduleTimeout(() => {
            highlighted.forEach(m => setPhase(m.colKey, m.name, 'landing'));
        }, dataSwapAt + ANIM.MOVE_MS);

        // 4) After LANDING_MS, glow linger (green tint stays visible)
        scheduleTimeout(() => {
            highlighted.forEach(m => setPhase(m.colKey, m.name, 'glow'));
        }, dataSwapAt + ANIM.MOVE_MS + ANIM.LANDING_MS);

        // 5) Clear all phases after glow linger. Movers-down cleared here too.
        const total = _seqLen(highlighted.length);
        scheduleTimeout(() => {
            highlighted.forEach(m => setPhase(m.colKey, m.name, null));
            down.forEach(m => setPhase(m.colKey, m.name, null));
        }, dataSwapAt + ANIM.MOVE_MS + ANIM.LANDING_MS + ANIM.GLOW_LINGER_MS);

        animBusyUntilRef.current = Date.now() + total;
    };

    const snapshotAndApply = (newData) => {
        // Save the NEW ranks as the prev snapshot BEFORE swapping the display.
        // Inline write (not inside setData) so the next tick's diff compares
        // fresh vs fresher, not stale.
        const nextSnap = { pickers: {}, pressers: {}, packers: {} };
        for (const col of ['pickers', 'pressers', 'packers']) {
            for (const r of (newData.columns[col] || [])) nextSnap[col][r.name] = r.rank;
        }
        prevSnapshotRef.current = nextSnap;
        setData(newData);
    };

    // ─── Poll every 5s ──────────────────────────────────────────────
    useEffect(() => {
        let cancelled = false;
        let intervalId = null;
        async function tick() {
            try {
                const r = await fetch(`/api/rankings/display?token=${encodeURIComponent(token)}`);
                if (!r.ok) throw new Error(`${r.status}`);
                const j = await r.json();
                if (cancelled) return;
                setPollAlive(true);
                setError('');
                // If a sequence is still playing, hold this response until
                // it ends. Only keep the LATEST response — no backlog.
                const now = Date.now();
                if (now < animBusyUntilRef.current) {
                    pendingDataRef.current = j;
                    return;
                }
                runSequence(j);
            } catch (e) {
                if (String(e.message) === '401') {
                    setError('Display expired');
                    if (intervalId != null) { clearInterval(intervalId); intervalId = null; }
                    return;
                }
                setPollAlive(false);
            }
        }
        tick();
        intervalId = setInterval(() => {
            // Between polls, if we have a queued response and are now free,
            // process it before the next network fetch.
            if (pendingDataRef.current && Date.now() >= animBusyUntilRef.current) {
                const queued = pendingDataRef.current;
                pendingDataRef.current = null;
                runSequence(queued);
            } else {
                tick();
            }
        }, POLL_MS);
        return () => {
            cancelled = true;
            if (intervalId != null) clearInterval(intervalId);
            clearScheduledTimeouts();
        };
    }, [token, muted]);

    if (error) {
        return <div className="board-expired">Display expired — regenerate from admin dashboard.</div>;
    }

    const prevRanks = prevSnapshotRef.current;

    // Location TZ for kill feed timestamps (matches header clock)
    const feedTz = locationTZ(data?.location_name);

    return (
        <div className="board-root">
            <ParticleBackground />
            <AnimatePresence>
                {showcaseVisible && (
                    <Showcase data={data} onDismiss={() => setShowcaseVisible(false)} />
                )}
            </AnimatePresence>
            <AnimatePresence>
                {trophy && <AchievementPopup payload={trophy} />}
            </AnimatePresence>
            <AnimatePresence>
                {milestoneShown && <MilestoneTakeover payload={milestoneShown} locationName={data?.location_name} />}
            </AnimatePresence>
            <AnimatePresence>
                {spotlight && <Spotlight payload={spotlight} />}
            </AnimatePresence>
            <AnimatePresence>
                {battle && <Battle payload={battle} />}
            </AnimatePresence>
            <header className="board-header">
                <div className="board-header__logo">
                    <img src="/logos/soccer-post.svg" alt="Soccer Post" onError={(e) => { e.target.style.display = 'none'; }} />
                </div>
                {(() => {
                    const tz = locationTZ(data?.location_name);
                    const timeStr = clockNow.toLocaleTimeString('en-US', {
                        timeZone: tz, hour: '2-digit', minute: '2-digit', second: '2-digit',
                    });
                    const abbr = tzAbbrev(tz, clockNow);
                    return (
                        <>
                            <div className="board-header__brand-block">
                                <div className="board-header__brand">Leaderboard</div>
                                {flags.NARRATIVE && (
                                    <div className="board-header__narrative">{composeNarrative()}</div>
                                )}
                            </div>
                            <div className="board-header__location">
                                {data?.location_name || '…'}
                            </div>
                            <div className="board-header__clock">
                                <span className={`poll-dot ${pollAlive ? 'alive' : 'stalled'}`} />
                                <span>{timeStr}</span>
                                {abbr && <span className="board-header__tz">{abbr}</span>}
                            </div>
                        </>
                    );
                })()}
            </header>
            <main className="board-grid"
                  style={(() => {
                      // 2026-08-27: a token may select 1, 2, or 3 columns —
                      // the grid stretches whatever is present across the TV.
                      const n = data?.columns
                          ? COLUMNS.filter(c => c.key in data.columns).length || 3
                          : 3;
                      return { gridTemplateColumns: `repeat(${n}, 1fr)` };
                  })()}>
                {/* Render nothing until the first poll lands — otherwise the
                    board flashes all three columns before collapsing to the
                    token's selection. */}
                {(data?.columns ? COLUMNS.filter(col => col.key in data.columns) : []).map(col => {
                    const rows = data?.columns[col.key] || [];
                    // Ghost rows fill in only when the real count is under 15 — so
                    // a slow-day 6-row column still visually holds its space. Once
                    // there are 15+ real workers no ghosts are appended, and the
                    // auto-scroll takes care of the rest.
                    const ghosts = Math.max(0, 15 - rows.length);
                    const activeCallout = (calloutsByCol[col.key] || [])[0];
                    return (
                        <section key={col.key} className="board-col">
                            <h2 className="board-col__title">
                                <span className="board-col__icon">{COLUMN_ICONS[col.key]}</span>
                                {col.label}
                                <span className="board-col__count">: {rows.length}</span>
                            </h2>
                            <div className="board-col__callout-slot">
                                <AnimatePresence>
                                    {activeCallout && <Callout payload={activeCallout} />}
                                </AnimatePresence>
                            </div>
                            <LayoutGroup>
                                <div className="board-col__list" ref={listRefs[col.key]}>
                                  <div className="board-col__track" ref={trackRefs[col.key]}>
                                    <AnimatePresence>
                                        {rows.map((r, idx) => {
                                            const phase = phases[`${col.key}::${r.name}`] || 'idle';
                                            const prevRank = prevRanks[col.key]?.[r.name];
                                            const delta = (prevRank != null && r.rank !== prevRank) ? (prevRank - r.rank) : 0;
                                            // Anticipation progress: how close this row is to the row above's score.
                                            const rowAbove = rows[idx - 1];
                                            const above = rowAbove?.score ?? 0;
                                            const progressPct = above > 0 ? Math.min(1, r.score / above) : 1;
                                            const streak = streakForRow(col.key, r.name);
                                            const rowKey = `${col.key}::${r.name}`;
                                            const level = flags.LEVELS ? Math.floor(r.score / pointsPerLevel(col.key)) : null;
                                            const levelFlash = levelFlashKey === rowKey;
                                            const title = titleFor(col.key, r);
                                            const inBattle = battle
                                                && (battle.leader?.name === r.name || battle.chaser?.name === r.name);
                                            return (
                                                <RankRow
                                                    key={`${col.key}-${r.name}`}
                                                    row={r}
                                                    prevRank={prevRank}
                                                    phase={phase}
                                                    delta={delta}
                                                    progressPct={progressPct}
                                                    streak={streak}
                                                    showAvatar={flags.AVATARS}
                                                    level={level}
                                                    levelFlash={levelFlash}
                                                    title={title}
                                                    inBattle={inBattle}
                                                />
                                            );
                                        })}
                                    </AnimatePresence>
                                    {[...Array(ghosts)].map((_, i) => <EmptyRow key={`ghost-${i}`} />)}
                                  </div>
                                </div>
                            </LayoutGroup>
                        </section>
                    );
                })}
            </main>
            {flags.KILL_FEED && <KillFeed items={killFeed} tzForClock={feedTz} />}
        </div>
    );
}
